// Line-count audit (2026-05-04): 795 lines.
import { useCallback } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { propertyModificationService } from '@/services/ai/PropertyModificationService';
import { textCommandHandler } from '@/services/ai/commands/handlers/TextCommandHandler';
import { commandStateManager } from '@/services/ai/CommandStateManager';
import { flexRelationshipService } from '@/services/ai/FlexRelationshipService';
import { undoRedoService } from '@/services/ai/UndoRedoService';
import { clarificationService } from '@/services/ai/ClarificationService';
import { Component, ComponentInput, ComponentType } from '@/types';
import type { PropertyMessage } from '@/components/ai/types';
import type { PropertyChange as FlexPropertyChange } from '@/services/ai/FlexRelationshipService';
import type { PropertyChange } from '@/services/ai/commands/BaseCommandHandler';
import type {
  PreBackendMessageResult,
  UsePreBackendCommandsDeps,
  UsePreBackendCommandsReturn,
} from './agentMessagingTypes';
import {
  getBlockedAICommandPermissionMessage,
  getMutationCategoriesForChanges,
} from '@/services/ai/agentCommandPermissions';

type LocalPropertyChange = PropertyChange | FlexPropertyChange;

function toPropertyChangeRecord(
  changes: LocalPropertyChange | undefined
): Record<string, unknown> | undefined {
  if (!changes) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(changes));
}

function toHistoryPropertyChanges(
  changes: LocalPropertyChange | undefined
): Record<string, unknown> | undefined {
  return toPropertyChangeRecord(changes);
}

function formatAppliedChangeDescription(changes: PropertyChange): string {
  return Object.entries(changes)
    .filter(([key]) => !key.startsWith('_'))
    .map(([key, value]) => {
      if (key === 'responsiveStyles' && typeof value === 'object' && value !== null) {
        return Object.entries(value)
          .map(([breakpoint, styles]) => {
            if (typeof styles === 'object' && styles !== null) {
              const styleList = Object.entries(styles)
                .filter(([styleKey, styleValue]) => styleKey !== 'background' || styleValue !== undefined)
                .map(([styleKey, styleValue]) => `${styleKey}: ${styleValue}`)
                .join(', ');
              return `${breakpoint}: ${styleList}`;
            }

            return `${breakpoint} styles`;
          })
          .join('; ');
      }

      if (typeof value === 'object' && value !== null) {
        return Object.entries(value)
          .filter(([nestedKey]) => !nestedKey.startsWith('_'))
          .map(([nestedKey, nestedValue]) =>
            typeof nestedValue === 'object' && nestedValue !== null
              ? `${nestedKey}: ${JSON.stringify(nestedValue)}`
              : `${nestedKey}: ${nestedValue}`
          )
          .join(', ');
      }

      return `${key}: ${value}`;
    })
    .filter(Boolean)
    .join(', ');
}

async function parseRuntimeQuickChanges(
  userInput: string,
  targetComponent: Component
) {
  return propertyModificationService.parsePropertyChangesAsync(userInput, targetComponent);
}

export type HomeAssistantAddKind =
  | 'status-card'
  | 'light-control'
  | 'thermostat'
  | 'energy-metric'
  | 'device-health';

const HOME_ASSISTANT_ADD_COMMANDS: Array<{
  command: string;
  kind: HomeAssistantAddKind;
}> = [
  {
    command: 'add a read-only Home Assistant entity status card inside this selected component',
    kind: 'status-card',
  },
  {
    command: 'add an inert Home Assistant light control block inside this selected component',
    kind: 'light-control',
  },
  {
    command: 'add an inert Home Assistant thermostat block inside this selected component',
    kind: 'thermostat',
  },
  {
    command: 'add a read-only Home Assistant energy metric block inside this selected component',
    kind: 'energy-metric',
  },
  {
    command: 'add a read-only Home Assistant device health block inside this selected component',
    kind: 'device-health',
  },
];

const HOME_ASSISTANT_ADD_TARGET_TYPES = new Set<ComponentType>([
  ComponentType.CONTAINER,
  ComponentType.FLEX_CONTAINER,
  ComponentType.CARD,
  ComponentType.DATA_CARD,
  ComponentType.FORM_CONTAINER,
]);

export function getHomeAssistantAddKind(userInput: string): HomeAssistantAddKind | null {
  const command = HOME_ASSISTANT_ADD_COMMANDS.find((candidate) =>
    candidate.command === userInput
  );

  return command?.kind ?? null;
}

function createHomeAssistantChildComponent(
  kind: HomeAssistantAddKind,
  target: Component,
  order: number,
): ComponentInput {
  if (kind === 'light-control') {
    return {
      name: 'Home Assistant Light Control',
      type: ComponentType.SLIDER,
      pageId: target.pageId,
      parentId: target.id,
      properties: {
        label: 'Light Dimmer',
        value: 50,
        min: 0,
        max: 100,
        step: 5,
        unit: '%',
        displayMode: 'vertical-gauge',
        orientation: 'vertical',
        showValue: true,
        showMinMax: true,
        showTicks: true,
        tickInterval: 25,
        tickPlacement: 'outside',
        theme: 'gradient',
        trackColor: '#e5e7eb',
        activeTrackColor: '#facc15',
        thumbColor: '#f59e0b',
        action: {
          type: 'manualOnly',
          manualOnly: true,
          safetyNote:
            'Manual-only Home Assistant dimmer placeholder. Bind an entity for review; no service call is executed by this component.',
        },
      },
      size: { width: 112, height: 220 },
      style: {
        backgroundColor: 'transparent',
      },
      responsivePosition: {
        strategy: 'relative',
        responsiveSize: {
          width: 'auto',
          height: 'auto',
        },
      },
      children: [],
      order,
    };
  }

  if (kind === 'thermostat') {
    return {
      name: 'Home Assistant Thermostat',
      type: ComponentType.THERMOSTAT,
      pageId: target.pageId,
      parentId: target.id,
      properties: {
        label: 'Thermostat',
        temperature: 72,
        targetTemperature: 72,
        humidity: 45,
        min: 50,
        max: 90,
        unit: 'fahrenheit',
        mode: 'auto',
        theme: 'nest',
        action: {
          type: 'manualOnly',
          manualOnly: true,
          safetyNote:
            'Inert Home Assistant thermostat placeholder. Bind an entity for review; no climate service call is executed by this component.',
        },
      },
      size: { width: 260, height: 320 },
      style: {
        backgroundColor: 'transparent',
      },
      responsivePosition: {
        strategy: 'relative',
        responsiveSize: {
          width: 'auto',
          height: 'auto',
        },
      },
      children: [],
      order,
    };
  }

  if (kind === 'energy-metric') {
    return {
      name: 'Home Assistant Energy Metric',
      type: ComponentType.GAUGE_CHART,
      pageId: target.pageId,
      parentId: target.id,
      properties: {
        title: 'Energy Metric',
        value: 0,
        min: 0,
        max: 100,
        unit: 'kWh',
        displayMode: 'ring',
        readOnly: true,
      },
      size: { width: 220, height: 180 },
      style: {
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
      },
      responsivePosition: {
        strategy: 'relative',
        responsiveSize: {
          width: '100%',
          height: 'auto',
        },
      },
      children: [],
      order,
    };
  }

  if (kind === 'device-health') {
    return {
      name: 'Home Assistant Device Health',
      type: ComponentType.LEVEL_INDICATOR,
      pageId: target.pageId,
      parentId: target.id,
      properties: {
        label: 'Device Health',
        value: 72,
        min: 0,
        max: 100,
        unit: '%',
        segments: 5,
        theme: 'battery',
        readOnly: true,
      },
      size: { width: 80, height: 140 },
      style: {
        backgroundColor: 'transparent',
      },
      responsivePosition: {
        strategy: 'relative',
        responsiveSize: {
          width: 'auto',
          height: 'auto',
        },
      },
      children: [],
      order,
    };
  }

  return {
    name: 'Home Assistant Entity Status',
    type: ComponentType.DATA_TEXT,
    pageId: target.pageId,
    parentId: target.id,
    properties: {
      label: 'Entity State',
      value: 'Waiting for binding',
      valueField: 'value',
      readOnly: true,
    },
    size: { width: 180, height: 64 },
    style: {
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '12px',
    },
    responsivePosition: {
      strategy: 'relative',
      responsiveSize: {
        width: '100%',
        height: 'auto',
      },
    },
    children: [],
    order,
  };
}

export function usePreBackendCommands(
  deps: UsePreBackendCommandsDeps
): UsePreBackendCommandsReturn {
  const {
    component,
    multipleComponents,
    setMessages,
    setInputValue,
    setIsLoading,
    persistMessage,
    generateMessageId,
    addToHistory,
    applyPropertyChanges,
    updateComponent,
    commandPermissionGuard,
  } = deps;

  const handleLocalPreBackendCommands = useCallback(
    async (userInput: string): Promise<PreBackendMessageResult> => {
      const appendUserMessage = async (): Promise<void> => {
        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => [...previous, userMessage]);
        await persistMessage(userMessage);
        setInputValue('');
      };

      const appendBlockedAssistantMessage = async (content: string): Promise<void> => {
        const assistantMessage: PropertyMessage = {
          id: generateMessageId(),
          content,
          role: 'assistant',
          timestamp: new Date(),
          applied: false,
        };
        setMessages((previous) => [...previous, assistantMessage]);
        await persistMessage(assistantMessage);
        setIsLoading(false);
      };

      const componentsForFlexCheck = multipleComponents && multipleComponents.length >= 1
        ? multipleComponents
        : component ? [component] : [];

      const homeAssistantAddKind = getHomeAssistantAddKind(userInput);
      if (homeAssistantAddKind && component) {
        // Intentional local exception: these commands insert inert placeholder
        // children and must not route through the generic property parser.
        const editorStore = useEditorStore.getState();
        const targetComponent = editorStore.components.find(
          (item) => item.id === component.id,
        ) || component;

        if (HOME_ASSISTANT_ADD_TARGET_TYPES.has(targetComponent.type)) {
          const insertionPermission = commandPermissionGuard?.({
            categories: ['insertion'],
            project: editorStore.project,
            targetComponents: [targetComponent],
          });

          await appendUserMessage();

          if (insertionPermission && !insertionPermission.allowed) {
            await appendBlockedAssistantMessage(
              getBlockedAICommandPermissionMessage(insertionPermission, 'insertion')
            );
            return { handled: true, effectiveComponent: targetComponent };
          }

          const childComponent = createHomeAssistantChildComponent(
            homeAssistantAddKind,
            targetComponent,
            targetComponent.children.length,
          );
          editorStore.addComponent(childComponent);
          editorStore.selectComponent(targetComponent.id);

          const assistantContentByKind: Record<HomeAssistantAddKind, string> = {
            'light-control': `Added a manual-only Home Assistant dimmer placeholder inside "${targetComponent.name}". It is unbound and does not execute Home Assistant services.`,
            thermostat: `Added an inert Home Assistant thermostat placeholder inside "${targetComponent.name}". It is manual-only, unbound, and does not execute Home Assistant climate services.`,
            'status-card': `Added an unbound, read-only Home Assistant status placeholder inside "${targetComponent.name}". Bind an entity when you are ready; no Home Assistant service or live data path is executed.`,
            'energy-metric': `Added an unbound, read-only Home Assistant energy metric placeholder inside "${targetComponent.name}". Bind an entity when you are ready; no Home Assistant service or live data path is executed.`,
            'device-health': `Added an unbound, read-only Home Assistant device health placeholder inside "${targetComponent.name}". Bind an entity when you are ready; no Home Assistant service or live data path is executed.`,
          };

          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: assistantContentByKind[homeAssistantAddKind],
            role: 'assistant',
            timestamp: new Date(),
            applied: true,
          };
          setMessages((previous) => [...previous, assistantMessage]);
          await persistMessage(assistantMessage);

          addToHistory({
            command: userInput,
            componentType: childComponent.type,
            success: true,
            propertyChanges: { parentId: targetComponent.id },
            componentId: targetComponent.id,
          });

          setIsLoading(false);
          return { handled: true, effectiveComponent: targetComponent };
        }
      }

      if (componentsForFlexCheck.length > 0) {
        const storeStateForFlex = useEditorStore.getState();
        const freshComponentsForFlex = componentsForFlexCheck
          .map((item) => storeStateForFlex.components.find((componentItem) => componentItem.id === item.id) || item);
        const allComponentsForFlex = storeStateForFlex.components;

        if (flexRelationshipService.isFlexChildCommand(userInput)) {
          // Intentional local exception: flex child commands can update parent
          // layout and sibling children, so the relationship-aware translator
          // runs before single-component command parsing.
          const flexRelationship = flexRelationshipService.analyzeMultipleFlexRelationships(
            freshComponentsForFlex,
            allComponentsForFlex
          );

          if (flexRelationship?.isFlexChild && flexRelationship.parentId) {
            const translation = flexRelationshipService.translateCommand(userInput, flexRelationship);

            if (translation) {
              const targetsToCheck = [
                allComponentsForFlex.find((item) => item.id === flexRelationship.parentId),
                ...(flexRelationship.siblingIds || [])
                  .map((id) => allComponentsForFlex.find((item) => item.id === id)),
              ].filter((item): item is Component => item !== undefined);
              const flexCategories = [
                ...getMutationCategoriesForChanges(toPropertyChangeRecord(translation.parentChanges)),
                ...(translation.childChanges || []).flatMap((childChange) =>
                  getMutationCategoriesForChanges(toPropertyChangeRecord(childChange))
                ),
              ];
              const flexPermission = commandPermissionGuard?.({
                categories: flexCategories.length > 0 ? flexCategories : ['layout'],
                project: storeStateForFlex.project,
                targetComponents: targetsToCheck,
              });

              await appendUserMessage();

              if (flexPermission && !flexPermission.allowed) {
                await appendBlockedAssistantMessage(
                  getBlockedAICommandPermissionMessage(flexPermission, 'layout')
                );
                return { handled: true, effectiveComponent: component };
              }

              if (translation.parentChanges) {
                const parentComponent = allComponentsForFlex.find((item) => item.id === flexRelationship.parentId);
                const parentUpdate: Record<string, unknown> = {};

                if (translation.parentChanges.style) {
                  parentUpdate.style = {
                    ...(parentComponent?.style || {}),
                    ...translation.parentChanges.style,
                  };
                }

                if (translation.parentChanges.properties) {
                  parentUpdate.properties = {
                    ...(parentComponent?.properties || {}),
                    ...translation.parentChanges.properties,
                  };
                }

                if (parentComponent) {
                  undoRedoService.recordAction(userInput, parentComponent, {
                    style: translation.parentChanges.style,
                    properties: translation.parentChanges.properties,
                  });
                }

                updateComponent(flexRelationship.parentId, parentUpdate);

                const updatedParent = useEditorStore.getState().components.find(
                  (item) => item.id === flexRelationship.parentId
                );
                if (updatedParent) {
                  undoRedoService.captureAfterState(updatedParent);
                }
              }

              if (translation.childChanges?.length) {
                const siblingIds = flexRelationship.siblingIds || [];

                for (let index = 0; index < translation.childChanges.length && index < siblingIds.length; index += 1) {
                  const childChange = translation.childChanges[index];
                  const childId = siblingIds[index];

                  if (!childId || !childChange) {
                    continue;
                  }

                  const childUpdate: Record<string, unknown> = {};
                  if (childChange.style) {
                    childUpdate.style = childChange.style;
                  }
                  if (childChange.properties) {
                    childUpdate.properties = childChange.properties;
                  }

                  if (Object.keys(childUpdate).length > 0) {
                    updateComponent(childId, childUpdate);
                  }
                }
              }

              const assistantMessage: PropertyMessage = {
                id: generateMessageId(),
                content: translation.description,
                role: 'assistant',
                timestamp: new Date(),
                applied: true,
              };
              setMessages((previous) => [...previous, assistantMessage]);
              await persistMessage(assistantMessage);

              addToHistory({
                command: userInput,
                componentType: freshComponentsForFlex[0]?.type || 'flex-container',
                success: true,
                propertyChanges: toHistoryPropertyChanges(translation.parentChanges) || {},
                componentId: flexRelationship.parentId ?? undefined,
              });

              setIsLoading(false);
              return { handled: true, effectiveComponent: component };
            }
          }
        }
      }

      const targetComponentForQuickCmd = component || multipleComponents?.[0];
      if (targetComponentForQuickCmd) {
        const freshStoreState = useEditorStore.getState();
        const freshTargetComponent = freshStoreState.components.find(
          (item) => item.id === targetComponentForQuickCmd.id
        );

        if (freshTargetComponent) {
          commandStateManager.captureState(freshTargetComponent);
        }

        const activeTextSelection = useEditorStore.getState().getTextSelection();
        let textComponentFromSelection: Component | undefined;
        if (activeTextSelection?.componentId) {
          textComponentFromSelection = useEditorStore.getState().components.find(
            (item) => item.id === activeTextSelection.componentId
          );
        }

        if (
          activeTextSelection &&
          activeTextSelection.selectedText &&
          !activeTextSelection.isCursor &&
          textComponentFromSelection?.type === ComponentType.TEXT
        ) {
          // Intentional local exception: selected text range edits need the
          // selection context that the canonical component parser does not carry.
          const selectionResult = await textCommandHandler.parseCommand(
            userInput,
            textComponentFromSelection,
            activeTextSelection
          );

          if (selectionResult.success && selectionResult.command?.changes) {
            const textPermission = commandPermissionGuard?.({
              categories: getMutationCategoriesForChanges(
                toPropertyChangeRecord(selectionResult.command.changes)
              ),
              project: useEditorStore.getState().project,
              targetComponents: [textComponentFromSelection],
            });

            await appendUserMessage();

            if (textPermission && !textPermission.allowed) {
              await appendBlockedAssistantMessage(
                getBlockedAICommandPermissionMessage(textPermission, 'content')
              );
              return { handled: true, effectiveComponent: component };
            }

            undoRedoService.recordAction(
              userInput,
              textComponentFromSelection,
              selectionResult.command.changes
            );

            applyPropertyChanges(generateMessageId(), {
              ...selectionResult.command.changes,
              targetComponentId: textComponentFromSelection.id,
            });

            const updatedComponent = useEditorStore.getState().components.find(
              (item) => item.id === textComponentFromSelection.id
            );
            if (updatedComponent) {
              undoRedoService.captureAfterState(updatedComponent);
            }

            useEditorStore.getState().clearTextSelection();

            const assistantMessage: PropertyMessage = {
              id: generateMessageId(),
              content: selectionResult.command.description || `Applied "${userInput}" to selected text "${activeTextSelection.selectedText}"`,
              role: 'assistant',
              timestamp: new Date(),
              applied: true,
            };
            setMessages((previous) => [...previous, assistantMessage]);
            await persistMessage(assistantMessage);

            addToHistory({
              command: userInput,
              componentType: textComponentFromSelection.type,
              success: true,
            });

            return { handled: true, effectiveComponent: component };
          }
        }

        const quickChanges = await parseRuntimeQuickChanges(
          userInput,
          freshTargetComponent || targetComponentForQuickCmd
        );

        if (quickChanges && Object.keys(quickChanges).length > 0) {
          const targetComponent = freshTargetComponent || targetComponentForQuickCmd;
          if (quickChanges.properties?.stacked !== undefined && targetComponent?.type !== 'bar-chart') {
            await appendUserMessage();

            const errorMessage: PropertyMessage = {
              id: generateMessageId(),
              content: `Stacking is only available for bar charts. This component is a ${targetComponent?.type || 'unknown'}.`,
              role: 'assistant',
              timestamp: new Date(),
            };
            setMessages((previous) => [...previous, errorMessage]);
            await persistMessage(errorMessage);

            return { handled: true, effectiveComponent: component };
          }

          const componentsToUpdate = multipleComponents && multipleComponents.length >= 2
            ? multipleComponents
            : [targetComponentForQuickCmd];
          const quickPermission = commandPermissionGuard?.({
            categories: getMutationCategoriesForChanges(toPropertyChangeRecord(quickChanges)),
            project: useEditorStore.getState().project,
            targetComponents: componentsToUpdate,
          });

          await appendUserMessage();

          if (quickPermission && !quickPermission.allowed) {
            await appendBlockedAssistantMessage(
              getBlockedAICommandPermissionMessage(quickPermission, 'content')
            );
            return { handled: true, effectiveComponent: component };
          }

          if (quickChanges.properties?._presetInfo === true) {
            const assistantMessage: PropertyMessage = {
              id: generateMessageId(),
              content: propertyModificationService.describeChanges(quickChanges),
              role: 'assistant',
              timestamp: new Date(),
            };
            setMessages((previous) => [...previous, assistantMessage]);
            await persistMessage(assistantMessage);

            return { handled: true, effectiveComponent: component };
          }

          for (const currentComponent of componentsToUpdate) {
            const freshComponentState = useEditorStore.getState().components.find(
              (item) => item.id === currentComponent.id
            );
            if (freshComponentState) {
              undoRedoService.recordAction(userInput, freshComponentState, {
                style: quickChanges.style,
                properties: quickChanges.properties,
                size: quickChanges.size,
                responsivePosition: quickChanges.responsivePosition,
              });
            }

            applyPropertyChanges(generateMessageId(), {
              ...quickChanges,
              targetComponentId: currentComponent.id,
            });

            if (quickChanges.size || quickChanges.style) {
              commandStateManager.updateCacheWithChanges(currentComponent.id, {
                size: quickChanges.size || {},
                style: quickChanges.style || {},
              });
            }

            const updatedComponent = useEditorStore.getState().components.find(
              (item) => item.id === currentComponent.id
            );
            if (updatedComponent) {
              undoRedoService.captureAfterState(updatedComponent);
            }
          }

          const automationListDescription = typeof quickChanges.properties?._automationRuleListDescription === 'string'
            ? quickChanges.properties._automationRuleListDescription
            : undefined;

          const changeDescription = formatAppliedChangeDescription(quickChanges);
          const warningContent = quickChanges._warnings?.join('\n');
          const assistantContent = automationListDescription
            ? String(automationListDescription)
            : [
                `✅ Applied: ${changeDescription}${componentsToUpdate.length > 1 ? ` (to ${componentsToUpdate.length} components)` : ''}`,
                warningContent,
              ]
                .filter(Boolean)
                .join('\n\n');

          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: assistantContent,
            role: 'assistant',
            timestamp: new Date(),
            applied: true,
          };
          setMessages((previous) => [...previous, assistantMessage]);
          await persistMessage(assistantMessage);

          addToHistory({
            command: userInput,
            componentType: componentsToUpdate[0]?.type || 'unknown',
            success: true,
            propertyChanges: toHistoryPropertyChanges(quickChanges),
            componentId: componentsToUpdate[0]?.id,
          });

          setIsLoading(false);
          return { handled: true, effectiveComponent: component };
        }
      }

      if (clarificationService.isCorrection(userInput)) {
        const correction = clarificationService.parseCorrection(userInput);

        if (correction.type === 'undo') {
          const undoMessage: PropertyMessage = {
            id: generateMessageId(),
            content: 'Undo functionality is coming soon. For now, you can manually adjust the component.',
            role: 'assistant',
            timestamp: new Date(),
          };
          setMessages((previous) => [...previous, undoMessage]);
          await persistMessage(undoMessage);
          setInputValue('');
          return { handled: true, effectiveComponent: component };
        }
      }

      return { handled: false, effectiveComponent: component };
    },
    [
      component,
      multipleComponents,
      setMessages,
      setInputValue,
      setIsLoading,
      persistMessage,
      generateMessageId,
      addToHistory,
      applyPropertyChanges,
      updateComponent,
      commandPermissionGuard,
    ]
  );

  return { handleLocalPreBackendCommands };
}
